import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { map, mergeMap, catchError } from 'rxjs/operators';
import { WalletApiService } from '@core/services/api/wallet-api.service';
import { GrandCompteService } from '@core/services/grand-compte.service';
import * as WalletActions from './wallet.actions';

@Injectable()
export class WalletEffects {
    private actions$ = inject(Actions);
    private translate = inject(TranslateService);
    private walletApi = inject(WalletApiService);
    private grandCompteService = inject(GrandCompteService);

    loadWallets$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.loadWallets),
            mergeMap(({ clientIds, grandCompteId, currency }) => {
                const grandCompte = this.grandCompteService.currentGrandCompte();

                if (!grandCompte || !grandCompte.advertisers) {
                    return of(WalletActions.loadWalletsSuccess({ wallets: [] }));
                }

                return this.walletApi.getWallets(clientIds, currency).pipe(
                    map((walletsResponse) => {
                        const walletDataArray = clientIds.map((clientId) => {
                            const advertiser = grandCompte.advertisers.find((a) => a.client_id === clientId);
                            const wallet = walletsResponse.find((w) => w && w.id && w.client_id.toString() === clientId) || null;

                            const walletData = {
                                clientId,
                                advertiserName: advertiser?.advertiser_name || '',
                                grandCompteId,
                                wallet,
                            };
                            return walletData;
                        });

                        return WalletActions.loadWalletsSuccess({
                            wallets: walletDataArray,
                        });
                    }),
                    catchError((error) => {
                        if (error.status === 404 || error.code === 404) {
                            return of(
                                WalletActions.loadWalletsSuccess({
                                    wallets: [],
                                }),
                            );
                        } else {
                            return of(
                                WalletActions.loadWalletsFailure({
                                    error: error.message || this.translate.instant('storeErrors.loadWallets'),
                                }),
                            );
                        }
                    }),
                );
            }),
        ),
    );

    loadWalletBalance$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.loadWalletBalance),
            mergeMap(({ clientId, walletId, currency }) =>
                this.walletApi.getWalletBalance(clientId, walletId, currency).pipe(
                    map((balance) =>
                        balance === null
                            ? WalletActions.loadWalletBalanceFailure({ error: this.translate.instant('storeErrors.loadBalance') })
                            : WalletActions.loadWalletBalanceSuccess({
                                  walletId,
                                  balance,
                              }),
                    ),
                    catchError((error) =>
                        of(
                            WalletActions.loadWalletBalanceFailure({
                                error: error.message || this.translate.instant('storeErrors.loadBalance'),
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    loadFacturations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.loadFacturations),
            mergeMap(({ clientIds, grandCompteId }) => {
                const grandCompte = this.grandCompteService.currentGrandCompte();

                if (!grandCompte || !grandCompte.advertisers) {
                    return of(
                        WalletActions.loadFacturationsSuccess({
                            facturations: [],
                        }),
                    );
                }

                return this.walletApi.getFacturations(clientIds).pipe(
                    map((facturationsResponse) => {
                        const facturationDataArray = clientIds.map((clientId) => {
                            const advertiser = grandCompte.advertisers.find((a) => a.client_id === clientId);
                            const facturation = facturationsResponse.find((f) => f && f.client_id === clientId) || null;
                            return {
                                clientId,
                                advertiserName: advertiser?.advertiser_name || '',
                                grandCompteId,
                                facturation,
                            };
                        });

                        return WalletActions.loadFacturationsSuccess({
                            facturations: facturationDataArray,
                        });
                    }),
                    catchError((error) =>
                        of(
                            WalletActions.loadFacturationsFailure({
                                error: error.message || this.translate.instant('storeErrors.loadFacturations'),
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    updateFacturation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WalletActions.updateFacturation),
            mergeMap(({ clientId, facturation }) =>
                this.walletApi.updateFacturation(clientId, facturation).pipe(
                    map(() =>
                        WalletActions.updateFacturationSuccess({
                            clientId,
                            facturation,
                        }),
                    ),
                    catchError((error) =>
                        of(
                            WalletActions.updateFacturationFailure({
                                error: error.message || this.translate.instant('storeErrors.updateFacturation'),
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );
}
